import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import usePagedProperties from 'LearningManager/hooks/usePagedProperties';
import { useStores } from 'Hooks/useStore';
import {
	filterRolesForUser,
	roleDescription,
	roleDisplayLabel,
	visibleRoles
} from 'LearningManager/helpers/role';
import { Status } from 'app-types';

const Role = observer(() => {
	const { assignUsersStore } = useStores();
	const { roles, status } = assignUsersStore;
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);
	const { dropdownProps: propertyDropdownProps } = usePagedProperties(
		tenantId,
		isTenantResolved
	);

	const {
		draft: {
			customSetup: {
				selectedRoles,
				selectedRolePropertyIds,
				allRolePropertiesSelected
			}
		},
		toggleAssignUsersSelection,
		setAssignUsersSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');
	const { t } = useTranslation();

	// Roles are directory-wide, so only the property filter follows the tenant.
	useEffect(() => {
		if (!roles.length && status.fetchRoles !== Status.Pending) {
			assignUsersStore.fetchRoles().catch(() => undefined);
		}
	}, []);

	const selectableRoles = useMemo(() => visibleRoles(roles), [roles]);

	const filtered = useMemo(() => filterRolesForUser(roles, search), [
		roles,
		search
	]);

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const numId = Number(id);
		const role = selectableRoles.find((r) => r.id === numId);
		if (!role) return;
		if (checked !== selectedRoles.some((r) => r.id === numId)) {
			toggleAssignUsersSelection('selectedRoles', {
				id: role.id,
				name: role.name
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedRoles.length === filtered.length) {
			setAssignUsersSelection('selectedRoles', []);
		} else {
			setAssignUsersSelection(
				'selectedRoles',
				filtered.map((r) => ({ id: r.id, name: r.name }))
			);
		}
	};

	const isAllSelected =
		filtered.length > 0 &&
		filtered.every((r) => selectedRoles.some((sr) => sr.id === r.id));
	const isLoading = status.fetchRoles === Status.Pending;

	return (
		<div id="lms-ca-assign-users-role">
			<div>
				<Heading />
				<div className={styles.filters}>
					<div id="lms-ca-au-role-property-filter" className={styles.search}>
						<div className={styles.selectProp}>
							{t('learning.assignUsers.options.group.selectProperties')}
						</div>
						<MultiSelectDropdown
							{...propertyDropdownProps}
							searchable
							searchPlaceholder={t(
								'learning.assignUsers.options.group.searchProperties'
							)}
							placeholder={t('learning.assignUsers.options.group.selectOption')}
							onChange={(ids, allSelected) =>
								setAssignUsersPropertyFilter(
									'selectedRolePropertyIds',
									ids,
									allSelected
								)
							}
							defaultSelectedIds={selectedRolePropertyIds}
							defaultAllSelected={allRolePropertiesSelected}
						/>
					</div>
					<Input.Search
						id="lms-ca-au-role-search"
						className={styles.search}
						placeholder={t('learning.assignUsers.options.group.search')}
						suffix={<Icon type="search" role="img" />}
						// Keeps a suffix rendered, so the input isn't remounted mid-typing.
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</div>
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title={t('learning.assignUsers.options.role.selectRole')}
					astrix={true}
				/>
				{filtered.length > 1 && (
					<div
						id="lms-ca-au-role-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isAllSelected
							? t('learning.assignUsers.options.group.deselectAll')
							: t('learning.assignUsers.options.group.selectAll')}
					</div>
				)}
			</div>
			<div id="lms-ca-au-role-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{filtered.map((role) => {
					const isChecked = selectedRoles.some((r) => r.id === role.id);
					return (
						<CheckBoxCard
							key={role.id}
							id={role.id}
							htmlId={`lms-ca-au-role-${role.id}`}
							icon={
								<Icon
									type={
										isChecked ? 'roleOptFilled' : 'roleOptIcon'
									}
								/>
							}
							title={t(roleDisplayLabel(role.name))}
							desc={t(roleDescription(role.name))}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
			</div>
		</div>
	);
});

export default Role;